import type { ContactInquiry } from "@diality/contracts";
import type { ContactInquiryResult } from "@diality/ui";
import { apiUrl, resolveApiEndpoint } from "./api-endpoint";
import { CAPTCHA_CONSENT_EVENT, hasCaptchaConsent } from "./captcha-consent";
import { acquireCaptchaToken } from "./recaptcha";

const REFUSED_MESSAGE = "We couldn't accept this submission. Please try again.";
const RETRY_MESSAGE =
  "We couldn't submit your message just now. Please try again in a moment.";
const RATE_MESSAGE =
  "You've sent several messages recently. Please wait 15 minutes before trying again.";

/** Bound optional Google work so an outage leaves time to deliver via fallback. */
async function optionalCaptchaToken(signal: AbortSignal): Promise<string | undefined> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) cancel();
  const timeout = setTimeout(cancel, 5000);
  try {
    const response = await fetch("/config.json", { signal: controller.signal });
    if (!response.ok) return undefined;
    const config = (await response.json()) as {
      recaptcha?: { siteKey?: unknown };
    } | null;
    const siteKey = config?.recaptcha?.siteKey;
    if (typeof siteKey !== "string" || !siteKey.trim()) return undefined;
    return await acquireCaptchaToken(siteKey, controller.signal);
  } catch {
    // Withdrawal / overall cancellation is checked before any submission.
    return undefined;
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", cancel);
  }
}

/** Every attempt obtains a new nonce/token, including retries after a mail failure. */
export async function submitContactInquiry(
  inquiry: ContactInquiry,
): Promise<ContactInquiryResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);
  const googleConsented = hasCaptchaConsent();
  const syncConsent = () => {
    if (googleConsented && !hasCaptchaConsent()) controller.abort();
  };
  window.addEventListener(CAPTCHA_CONSENT_EVENT, syncConsent);
  window.addEventListener("focus", syncConsent);
  try {
    const signal = controller.signal;
    const endpoint = await resolveApiEndpoint({ signal });
    const challenge = await fetch(
      apiUrl(endpoint, "/api/inquiries/challenge"),
      { signal, cache: "no-store" },
    );
    if (!challenge.ok) return { ok: false, message: RETRY_MESSAGE };
    const { formNonce } = (await challenge.json()) as { formNonce?: unknown };
    if (typeof formNonce !== "string")
      return { ok: false, message: RETRY_MESSAGE };
    let captchaToken: string | undefined;
    let protectionMode: "recaptcha" | "first_party" = "first_party";
    if (googleConsented) {
      captchaToken = await optionalCaptchaToken(signal);
      if (captchaToken) protectionMode = "recaptcha";
    }
    syncConsent();
    if (signal.aborted) throw new Error("Submission cancelled");
    const response = await fetch(apiUrl(endpoint, "/api/inquiries"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal,
      body: JSON.stringify({
        ...inquiry,
        formNonce,
        protectionMode,
        captchaToken,
      }),
    });
    if (response.ok) return { ok: true };
    if (response.status === 503) return { ok: false, message: RETRY_MESSAGE };
    if (response.status === 429) return { ok: false, message: RATE_MESSAGE };
    const body = (await response.json().catch(() => ({}))) as {
      fields?: string[];
    };
    return {
      ok: false,
      fields: body.fields,
      message: body.fields?.length ? undefined : REFUSED_MESSAGE,
    };
  } catch {
    return { ok: false, message: RETRY_MESSAGE };
  } finally {
    clearTimeout(timeout);
    window.removeEventListener(CAPTCHA_CONSENT_EVENT, syncConsent);
    window.removeEventListener("focus", syncConsent);
  }
}
